import { useEffect, useState, type FormEvent } from "react";
import { PageHeader, LoadingState, ErrorState } from "@/components/PageHeader";
import { Button, Input, Label } from "@/components/ui/form";
import { Card, CardBody, DataTable, DataTableHead, DataTableTd, DataTableTh } from "@/components/ui/card";
import { apiFetch } from "@/utils/api";

interface Seller {
  id: string;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
}

export function AdminSellersPage() {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ companyName: "", contactName: "", email: "", phone: "", notes: "" });

  const load = () => apiFetch<{ sellers: Seller[] }>("/sellers").then((d) => setSellers(d.sellers));

  useEffect(() => {
    load().catch((e) => setError(e instanceof Error ? e.message : "Failed")).finally(() => setLoading(false));
  }, []);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    await apiFetch("/sellers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        companyName: form.companyName,
        contactName: form.contactName,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
      }),
    });
    setShowNew(false);
    setForm({ companyName: "", contactName: "", email: "", phone: "", notes: "" });
    await load();
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div>
      <PageHeader title="Lumber Sellers" actions={<Button onClick={() => setShowNew(!showNew)}>{showNew ? "Cancel" : "Add Seller"}</Button>} />
      {showNew && (
        <Card className="mb-6 max-w-lg">
          <CardBody>
            <form onSubmit={create} className="space-y-4">
              <div><Label>Company Name</Label><Input value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} required /></div>
              <div><Label>Contact Name</Label><Input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <Button type="submit">Create</Button>
            </form>
          </CardBody>
        </Card>
      )}
      <Card>
        <CardBody className="pt-2">
          <DataTable>
            <DataTableHead>
              <DataTableTh>Company</DataTableTh>
              <DataTableTh>Contact</DataTableTh>
              <DataTableTh>Email</DataTableTh>
            </DataTableHead>
            <tbody>
              {sellers.map((s) => (
                <tr key={s.id} className="border-t border-gray-50 hover:bg-gray-50/80">
                  <DataTableTd className="font-semibold">{s.company_name}</DataTableTd>
                  <DataTableTd>{s.contact_name || "—"}</DataTableTd>
                  <DataTableTd>{s.email || "—"}</DataTableTd>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </CardBody>
      </Card>
    </div>
  );
}
